/** Social Composer static config — platform/export specs, background modes, AI
 * prompt presets, layout bundles, brand fonts/swatches — plus pure token/string
 * helpers (brandColor / escAttr / toHex). No DOM state, no side effects. */

/**
 * Platform output specs — exact export pixels (the canvas owns final size, so the
 * AI/source aspect only needs to be close). IG-first per bao 2026-06-26.
 * `ar` = the closest aspect_ratio the generate enum accepts for the AI background.
 * Default = 3:4 portrait (POD material match, bao 2026-06-26). `ar` = the closest
 * aspect_ratio the generate enum accepts for the AI background.
 */
export const PLATFORMS = [
  { id: 'portrait', name: 'Portrait 3:4', w: 1080, h: 1440, ar: '3:4' },
  { id: 'story', name: 'Story 9:16', w: 1080, h: 1920, ar: '9:16' },
  { id: 'square', name: 'Square 1:1', w: 1080, h: 1080, ar: '1:1' },
]

/** Background modes for the composed post: brand fill / design fill / AI scene. */
export const BG_MODES = [
  { id: 'brand', name: 'Brand color' },
  { id: 'fill', name: 'Design fill' },
  { id: 'ai', name: 'AI scene' },
]

/**
 * One-tap background vibes — text-to-image (ideogram) renders an abstract
 * backdrop with NO design baked in; the design + text composite on top as
 * separate layers. why → keep generated-image and design/text as distinct
 * layers (seede research). NOTE the prompts are deliberately ABSTRACT gradient/
 * mesh/texture: scene/lifestyle prompts make ideogram draw a dominant subject
 * (a vase / book / sphere — judge-verified FAIL), which is unusable as a
 * backdrop. The "gradient mesh … no objects, no subject" formula judge-PASSed.
 * why → ai-bg prompt verification 2026-06-26.
 */
export const AI_PRESETS = [
  { name: 'Warm', prompt: 'abstract warm gradient mesh background, blended peach coral pink tones, soft, no objects, no subject, no text' },
  { name: 'Cool', prompt: 'abstract cool gradient mesh background, blended blue teal lavender tones, soft minimal, no objects, no subject, no text' },
  { name: 'Mono', prompt: 'abstract soft monochrome textured gradient background, subtle grain, neutral beige, no objects, no subject, no text' },
  { name: 'Bold', prompt: 'abstract bold vibrant gradient mesh background, saturated blended tones, modern, no objects, no subject, no text' },
]

/** Text-block position presets. */
export const POSITIONS = [
  { id: 'top', name: 'Top' },
  { id: 'center', name: 'Center' },
  { id: 'bottom', name: 'Bottom' },
]

/** Logo (wordmark) placement presets. */
export const LOGO_POS = [
  { id: 'off', name: 'Off' },
  { id: 'top', name: 'Top' },
  { id: 'bottom', name: 'Bottom' },
]

/**
 * Brand palette (from @mamamiya/brand tokens, resolved live off :root so we never
 * hardcode VI colours). Quick-apply swatches for the backdrop / text colour.
 */
export const BRAND_SWATCH_VARS = [
  '--brand-primary', '--brand-secondary-coral', '--brand-secondary-orange',
  '--brand-secondary-sky', '--brand-secondary-violet', '--brand-primary-soft',
]

/**
 * One-tap layout presets — each is a bundle of state applied together, then the
 * controls re-sync from state. Gives a "designed post" in one click. why → bao
 * slice-4 (IG layout presets).
 */
export const LAYOUTS = [
  { name: 'Editorial', s: { bgMode: 'brand', position: 'bottom', logo: 'top', font: 'Bricolage Grotesque' } },
  { name: 'Poster', s: { bgMode: 'ai', position: 'center', logo: 'bottom', font: 'Bricolage Grotesque' } },
  { name: 'Clean', s: { bgMode: 'brand', position: 'bottom', logo: 'bottom', font: 'Rubik' } },
  { name: 'Full bleed', s: { bgMode: 'fill', position: 'bottom', logo: 'bottom', font: 'Bricolage Grotesque' } },
]

/**
 * Brand fonts available to canvas — these are loaded by the app shell; we await
 * document.fonts before drawing so fillText actually uses them (not a fallback).
 */
export const FONTS = ['Bricolage Grotesque', 'Rubik']

/** Read a brand token (color) off :root so canvas fills honour VI, not hardcode. */
export function brandColor(varName, fallback) {
  const v = getComputedStyle(document.documentElement).getPropertyValue(varName).trim()
  return v || fallback
}

/** Escape a string for safe interpolation into an HTML attribute/text position. */
export function escAttr(s) { return String(s).replace(/[&<>"']/g, c => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c])) }

/** Normalise any CSS colour (oklch/named/rgb) to #rrggbb for <input type=color>. */
export function toHex(c) {
  if (/^#[0-9a-f]{6}$/i.test(c)) return c
  // Resolve oklch/named/rgb via a throwaway canvas so <input type=color> accepts it.
  try { const cx = document.createElement('canvas').getContext('2d'); cx.fillStyle = c; const v = cx.fillStyle; return /^#[0-9a-f]{6}$/i.test(v) ? v : '#ffffff' } catch { return '#ffffff' }
}
